import {
  normalizarStatusCadastro,
  TOTAL_ETAPAS_PADRAO,
  obterTotalEtapasPorPrograma,
} from "../mappers/statusMapper.js";
import {
  cadastroRequerSupex,
  normalizarListaSolicitacoesExcecao,
} from "../mappers/supexMapper.js";
import { obterEtapaRejeicaoPorHint } from "../mappers/rejeicaoMapper.js";
import { obterTipoProgramaUsuarioLogado } from "../utils/tipoPrograma.js";

/**
 * Resolve o resumo de status (label, etapa, progresso, situação) a partir
 * de um item de acompanhamento. Replica o contrato da função
 * `obterResumoStatusItem` do CadastroEmpresa.
 *
 * Aceita `tipoPrograma` opcional — quando ausente, lê do JWT do usuário
 * logado. Isso permite que a lista (sem detalhe carregado) e o detalhe
 * usem o mesmo programa, refletindo fielmente a regra de etapas:
 *   - programa 0 (TCE) ou 2 (TCA Empregador): etapa extra "Aguardando
 *     Preenchimento do Aprendiz" entre "Cadastro inicial" e "Validação
 *     do preenchimento";
 *   - programa 1 (TCA): fluxo padrão sem etapa extra.
 */

export function obterResumoStatusItem(item, tipoPrograma) {
  // O item da listagem pode vir com `raw` (após selecionar/atualizar) ou
  // sem ele (na lista inicial). Para que tanto a lista quanto o detalhe
  // usem o mesmo pipeline, mesclamos item + raw e damos prioridade a `raw`
  // (mais completo), mantendo o próprio `item` como fonte do campo
  // `status` e dos campos top-level quando o detalhe ainda não foi carregado.
  const raw = item?.raw && Object.keys(item.raw || {}).length > 0 ? item.raw : item || {};
  const fonte = { ...(item || {}), ...raw };

  const statusStr = String(
    fonte?.status ||
    raw?.status ||
    fonte?.status_label ||
    raw?.status_label ||
    fonte?.label ||
    raw?.label ||
    ""
  ).toUpperCase();

  const ehStatusAprendizOuEstudante =
    statusStr.includes("APRENDIZ") ||
    statusStr.includes("ESTUDANTE") ||
    statusStr.includes("EM_PREENCHIMENTO");

  const tpNum = Number(tipoPrograma);
  const totalRaw = Number(raw?.total_etapas ?? fonte?.total_etapas);

  const temEstudante =
    tpNum === 0 ||
    tpNum === 2 ||
    totalRaw === 5 ||
    ehStatusAprendizOuEstudante;

  const tipoProgramaEfetivo = temEstudante
    ? (Number(tipoPrograma) === 0 ? 0 : 2)
    : (Number.isFinite(Number(tipoPrograma))
        ? Number(tipoPrograma)
        : obterTipoProgramaUsuarioLogado());

  const listaExcecoes = normalizarListaSolicitacoesExcecao(fonte, raw);
  const requerSupex = cadastroRequerSupex(fonte, raw, listaExcecoes, item);
  const etapaRejeicaoHint = obterEtapaRejeicaoPorHint(
    fonte,
    raw,
    item,
    requerSupex,
    tipoProgramaEfetivo,
  );

  const status = normalizarStatusCadastro(
    fonte,
    raw,
    requerSupex,
    etapaRejeicaoHint,
    tipoProgramaEfetivo,
  );

  if (!status) {
    const isTca1 = !temEstudante && Number(tipoProgramaEfetivo) === 1;
    return {
      statusLabel: isTca1 ? "Aguardando validação do CIEEMG" : "Sem status",
      statusCor: isTca1 ? "#3b82f6" : "#94a3b8",
      etapaAtual: isTca1 ? 2 : 1,
      totalEtapas: temEstudante ? 5 : obterTotalEtapasPorPrograma(tipoProgramaEfetivo),
      progresso: isTca1 ? 50 : 0,
      situacao: null,
    };
  }

  const totalEtapasCalculo =
    Number(status.totalEtapas) ||
    (temEstudante ? 5 : (Number(tipoProgramaEfetivo) === 1 ? 4 : TOTAL_ETAPAS_PADRAO));

  const progresso = Math.max(
    0,
    Math.min(
      100,
      Math.round(
        (Number(status.etapaAtual || 1) / totalEtapasCalculo) * 100,
      ),
    ),
  );

  // Apenas duas categorias finais são expostas:
  //   - "finalizadas": processo chegou ao fim (APROVADO / FINALIZADO)
  //   - "canceladas": processo reprovado, recusado ou rejeitado
  // O default `null` indica "em andamento" sem precisar do termo explícito.
  return {
    statusLabel: status.statusLabel,
    statusCor: status.statusCor,
    etapaAtual: status.etapaAtual,
    totalEtapas: status.totalEtapas,
    progresso,
    situacao: status.finalizado
      ? "finalizadas"
      : status.rejeitado
        ? "canceladas"
        : null,
  };
}

export function filtrarAcompanhamentosPorSituacao(
  lista,
  situacao = "todos",
  tipoPrograma,
) {
  if (!Array.isArray(lista)) return [];
  if (!situacao || situacao === "todos") return lista;

  return lista.filter(
    (item) => obterResumoStatusItem(item, tipoPrograma).situacao === situacao,
  );
}

/**
 * Classifica categoricamente o status de um item de acompanhamento
 * em uma das 3 categorias dos filtros principais:
 * - "FINALIZADOS": processo aprovado/finalizado
 * - "ASSINATURA": processo aguardando assinaturas das partes
 * - "VALIDACAO": processos pendentes, aguardando preenchimento do estudante/aprendiz,
 *                validacao pelo CIEEMG ou confeccao de contrato
 */
export function classificarCategoriaStatus(item, tipoPrograma) {
  if (!item) return "VALIDACAO";

  const raw = item?.raw && Object.keys(item.raw || {}).length > 0 ? item.raw : item || {};
  const fonte = { ...(item || {}), ...raw };

  const tpNum = Number.isFinite(Number(tipoPrograma))
    ? Number(tipoPrograma)
    : Number(fonte?.tipo_programa ?? raw?.tipo_programa ?? 0);

  const resumo = obterResumoStatusItem(item, tpNum);

  const statusChave = String(
    resumo?.chave ||
    fonte?.status_canonico ||
    raw?.status_canonico ||
    fonte?.status ||
    raw?.status ||
    ""
  ).toUpperCase();

  const label = String(
    resumo?.statusLabel ||
    fonte?.status_label ||
    raw?.status_label ||
    fonte?.label ||
    raw?.label ||
    ""
  ).toUpperCase();

  const isFinalizado =
    resumo?.situacao === "finalizadas" ||
    statusChave === "FINALIZADO" ||
    statusChave === "APROVADO" ||
    statusChave === "CONTRATO_FINALIZADO" ||
    label.includes("FINALIZAD") ||
    label.includes("APROVAD");

  if (isFinalizado) {
    return "FINALIZADOS";
  }

  const isAssinatura =
    statusChave === "AGUARDANDO_ASSINATURAS" ||
    statusChave === "AGUARDANDO_ASSINATURA" ||
    statusChave === "DISPONIBILIZADO_PARA_ASSINATURA" ||
    statusChave === "DISPONIBILIZADO_PARA_ASSINATURAS" ||
    statusChave === "ASSINATURA" ||
    statusChave === "ASSINATURAS" ||
    statusChave.includes("ASSINATUR") ||
    label.includes("ASSINATUR");

  if (isAssinatura) {
    return "ASSINATURA";
  }

  return "VALIDACAO";
}

